import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const weaponImages = [
  'https://public.readdy.ai/ai/img_res/4ffbe57f21dd8a1234e5d64789f6ccd3.jpg',
  'https://readdy.ai/api/search-image?query=Panzerhaubitze%202000%20self%20propelled%20howitzer%20artillery%20system%2C%20military%20field%2C%20dark%20dramatic%20sky%2C%20powerful%20artillery%20piece%2C%20cinematic%20military%20photography%2C%20dark%20moody%20atmosphere%2C%20no%20people&width=600&height=400&seq=weapon-howitzer-001&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Lynx%20KF41%20infantry%20fighting%20vehicle%20modern%20armored%20vehicle%2C%20military%20green%2C%20dramatic%20lighting%2C%20dark%20background%2C%20powerful%20military%20machine%2C%20cinematic%20photography%2C%20no%20people&width=600&height=400&seq=weapon-lynx-001&orientation=landscape',
  'https://readdy.ai/api/search-image?query=anti%20aircraft%20defense%20system%20military%20vehicle%20with%20radar%20and%20cannon%2C%20dark%20night%20sky%2C%20dramatic%20lighting%2C%20modern%20military%20technology%2C%20cinematic%20photography%2C%20no%20people&width=600&height=400&seq=weapon-skyranger-001&orientation=landscape',
  'https://readdy.ai/api/search-image?query=artillery%20ammunition%20155mm%20shells%20production%20line%20military%20factory%2C%20rows%20of%20golden%20brass%20shells%2C%20industrial%20manufacturing%2C%20dramatic%20lighting%2C%20dark%20background%2C%20no%20people&width=600&height=400&seq=weapon-ammo-001&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Gepard%20anti%20aircraft%20tank%20flakpanzer%20military%20vehicle%20with%20twin%20cannons%2C%20dark%20dramatic%20sky%2C%20military%20field%2C%20cinematic%20photography%2C%20no%20people&width=600&height=400&seq=weapon-gepard-001&orientation=landscape',
];

const weaponSpecs = [
  ['Armor: composite multilayer', 'Gun: 120mm Rh-120 L/55', 'Speed: 72 km/h', 'Crew: 4'],
  ['Caliber: 155mm / 52 cal', 'Range: up to 67 km', 'Rate: 10 rds/min', 'Ammo: 60 rounds'],
  ['Gun: 35mm Wotan', 'Protection: STANAG 4569 Lvl 6', 'Crew: 3 + 8 troops', 'Weight: 38 t'],
  ['Gun: 30mm KCE', 'Rate: 1200 rds/min', 'Range: 3 km', 'Targets: UAV, heli, aircraft'],
  ['Caliber: 155mm', 'Range: up to 40 km', 'Output: 700,000/yr', 'CEP: < 10 m'],
  ['Guns: 2 × 35mm Oerlikon', 'Rate: 2 × 550 rds/min', 'Range: 5.5 km', 'Radar: search + track'],
];

const WeaponsSection = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const weapons = [1,2,3,4,5,6].map((n) => ({
    title:   t(`w${n}_title`),
    desc:    t(`w${n}_desc`),
    tag:     t(`w${n}_tag`),
    revenue: t(`w${n}_rev`),
    image:   weaponImages[n - 1],
    specs:   weaponSpecs[n - 1],
  }));

  return (
    <section id="weapons" className="py-24 bg-[#111111]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[#d4af37] text-xs tracking-widest uppercase font-medium">{t('weapons_badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t('weapons_title1')}<br />
            <span className="text-[#d4af37]">{t('weapons_title2')}</span>
          </h2>
          <p className="text-white/55 text-base max-w-2xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t('weapons_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {weapons.map((weapon, i) => (
            <div
              key={i}
              className={`group bg-white/3 border border-white/8 rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:border-[#d4af37]/40 hover:bg-white/5 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
              onClick={() => setActiveCard(activeCard === i ? null : i)}
            >
              <div className="relative h-48 overflow-hidden">
                <img src={weapon.image} alt={weapon.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className="bg-[#d4af37] text-black text-xs font-bold px-3 py-1 rounded-full tracking-wider uppercase">{weapon.tag}</span>
                </div>
                <div className="absolute bottom-3 right-3">
                  <span className="bg-black/60 border border-[#d4af37]/30 text-[#d4af37] text-xs px-3 py-1 rounded-full backdrop-blur-sm">{weapon.revenue}</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-white font-bold text-lg mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>{weapon.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>{weapon.desc}</p>
                <div className={`overflow-hidden transition-all duration-300 ${activeCard === i ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="border-t border-white/10 pt-4 mt-2">
                    <div className="text-[#d4af37] text-xs tracking-widest uppercase mb-2 font-medium">{t('weapons_specs_label')}</div>
                    <div className="grid grid-cols-1 gap-1.5">
                      {weapon.specs.map((spec, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <div className="w-1 h-1 bg-[#d4af37] rounded-full flex-shrink-0"></div>
                          <span className="text-white/60 text-xs">{spec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <button className="flex items-center gap-1.5 text-[#d4af37] text-xs tracking-wider uppercase mt-3 hover:gap-3 transition-all duration-200 cursor-pointer whitespace-nowrap">
                  <span>{activeCard === i ? t('weapons_hide') : t('weapons_show')}</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${activeCard === i ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WeaponsSection;